import firebase from 'firebase/app';
import shortid from 'shortid';
import { firestore } from 'core/firebase';
import FirebaseCollection from './collection';
import { EventEmitter } from 'events';
import FS from './events';

export default class FirebaseModel<T> extends EventEmitter {
  loaded: Boolean;
  key: String;
  parent: FirebaseCollection<this>;
  snapshot: firebase.firestore.DocumentSnapshot;

  static newKey(): String {
    return shortid.generate();
  }

  constructor(key: String, parent: FirebaseCollection<this>) {
    super();
    this.key = key;
    this.parent = parent;

    this.init = this.init.bind(this);
  }

  async init(snapshot: firebase.firestore.DocumentSnapshot) {
    this.loaded = true;
    this.snapshot = snapshot;
    this.emit(FS.updated, this);
  }

  ref(): firebase.firestore.DocumentReference {
    return firestore.doc(this.path());
  }

  onUpdate(cb: (model: FirebaseModel) => void): this {
    this.addListener(FS.updated, cb);
    if (this.loaded) cb(this);
    return this;
  }

  offUpdate(cb: (model: FirebaseModel) => void): this {
    this.removeListener(FS.updated, cb);
    return this;
  }

  async load(): Promise<T> {
    if (!this.loaded) {
      const snap = await this.ref().get();
      await this.init(snap);
    }

    return this.data();
  }

  exists() {
    return this.snapshot && this.snapshot.exists;
  }

  data(): T {
    const data = (this.snapshot && this.snapshot.data()) || {};
    data.key = this.key;
    return this.convert(data);
  }
  /**
   * Function which can be used to convert data before returning model
   * data is directly from snapshot
   */
  convert(data): T {
    return data;
  }

  update(update: T) {
    const ref = this.ref();
    if (this.parent.isBatchActive()) return this.parent._batch.update(ref, update);
    return ref.update(update);
  }

  updateAttr(attr, value) {
    return this.ref().update({ [attr]: value });
  }

  set(update: T, options: firebase.firestore.SetOptions) {
    const ref = this.ref();
    if (this.parent.isBatchActive()) return this.parent._batch.set(ref, update, options);
    return ref.set(update, options);
  }

  setAttr(attr, value) {
    return this.ref().set({ [attr]: value });
  }

  remove(): Promise<void> {
    return this.ref().delete();
  }

  path(attr?: String) {
    let p;
    if (this.parent) {
      p = this.parent.path() + '/' + this.key;
    } else {
      p = this.key;
    }
    if (attr) p += '.' + attr;

    return p;
  }

  watch() {
    if (!this.unsubscribe) {
      this.unsubscribe = this.ref().onSnapshot(this.init);
    }

    return this;
  }

  unwatch() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  async cloneSubCollection(
    collectionKey: String,
    newDoc: this,
    replaceKeyFunc?: (key: String) => String
  ) {
    const col: FirebaseCollection<this> = this[collectionKey];
    const newCol: FirebaseCollection<this> = newDoc[collectionKey];

    if (!col || !newCol) throw new Error('Can\'t find subcollection with key: ' + collectionKey);

    const models = await col.fetch();
    let counter = 0;
    newCol.batch();
    for (let subDoc of models) {
      const m = subDoc.data();
      delete m.key;
      const key = replaceKeyFunc ? replaceKeyFunc(subDoc.key) : subDoc.key;
      await newCol.get(key).set(m);
      counter++;
      if (counter > 400) {
        await newCol.commit();
        newCol.batch();
        counter = 0;
      }
    }

    await newCol.commit();
  }
}
